import { CHAT_FUNCTIONS } from '../../../config/chatFunctionNames';

/**
 * Check if a message is an organization events response.
 * Detection is purely by function_name — no text pattern matching.
 */
export function isOrganizationEventMessage(message) {
  return message?.function_name === CHAT_FUNCTIONS.ORGANIZATION_EVENTS;
}

/**
 * Extract event data from a message.
 * Prefers structured raw_output.events, falls back to text parsing
 * (only when function_name matches — safe, never false-matches).
 *
 * @returns {Array|null} Array of event objects or null
 */
export function getEventData(message) {
  if (!isOrganizationEventMessage(message)) return null;

  // Prefer structured data. The backend may deliver the array either nested
  // under raw_output (raw_output.events) or at the top level (response.events,
  // copied onto the message as message.events). Read both — image contract is
  // events[].image, already a full https URL.
  const events = message.raw_output?.events || message.events;
  if (Array.isArray(events) && events.length > 0) {
    return events;
  }

  // Fallback: parse text content for history messages lacking raw_output
  return parseEventsFromText(message.content);
}

/**
 * Extract header text before the numbered event list.
 * e.g. "Upcoming events at Kiln Boulder:" from full content.
 */
export function getEventHeader(content) {
  if (!content) return content;
  const parts = content.split(/\n\n\d+\.\s/);
  return parts[0].trim();
}

/**
 * Fallback parser for history messages that have function_name but no raw_output.
 * Parses the numbered text format:
 *   1. Title
 *      📅 date
 *      📍 location
 *      📝 description
 *
 * Only called when function_name already matched — safe, never false-matches.
 */
function parseEventsFromText(content) {
  if (!content) return null;

  // Split content into numbered items
  const itemPattern = /\n\n(\d+)\.\s+/;
  const parts = content.split(itemPattern);

  // parts[0] = header, then alternating: [number, item_text, number, item_text, ...]
  if (parts.length < 3) return null;

  const events = [];
  for (let i = 1; i < parts.length; i += 2) {
    const itemText = parts[i + 1];
    if (!itemText) continue;

    const lines = itemText.split('\n').map(l => l.trim()).filter(Boolean);
    const title = lines[0] || 'Untitled Event';

    // Extract fields by emoji prefix
    let formattedStart = '';
    let formattedEnd = '';
    let locationName = '';
    let description = '';
    let where = '';
    let image = '';

    for (const line of lines.slice(1)) {
      if (line.startsWith('📅')) {
        const dateText = line.replace('📅', '').trim();
        // Try to split "Mar 02, 2026 (9:30 AM - 6:30 PM) MST" into start/end
        formattedStart = dateText;
        formattedEnd = '';
      } else if (line.startsWith('📍')) {
        locationName = line.replace('📍', '').trim();
      } else if (line.startsWith('📌')) {
        where = line.replace('📌', '').trim();
      } else if (line.startsWith('🖼️')) {
        image = line.replace('🖼️', '').trim();
      } else if (line.startsWith('📝')) {
        description = line.replace('📝', '').trim();
      }
    }

    events.push({
      id: `text-event-${i}`,
      title,
      formatted_start: formattedStart,
      formatted_end: formattedEnd,
      location_name: locationName || null,
      description: description || null,
      where: where || null,
      image: image || null,
      is_ongoing: title.includes('[ONGOING]'),
      links: [],
      limit: null,
    });
  }

  return events.length > 0 ? events : null;
}
